import type { KAPLAYCtx } from '../types.ts'
import type { RoomOptions } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'

export function useWCB(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('wcB', `sprites/rooms/wc-b.png?${cacheStr}`)
  k.loadSprite('wcBHover', `sprites/rooms/wc-b--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON('wcBColliders', `colliders/wc-b.parsed.json?${cacheStr}`)

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function setWCB(roomOpts?: RoomOptions) {
    roomOpts = roomOpts || {}

    const roomObj = k.add()

    k.onLoad(() => {
      const { data } = collidersJson

      roomObj.add([
        k.sprite('wcB', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('background'),
        'wc_b_background',
        'background',
      ])

      roomObj.add([
        k.sprite('wcBHover', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('hover'),
        'wc_b_hover',
        'hover',
      ])

      data.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        roomObj.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      roomObj.add([
        k.level(
          [
            '                 ',
            '                 ',
            '                 ',
            '                 ',
            '           ?     ',
            '                 ',
            ' 1               ',
            '                 ',
            '                 ',
          ],
          {
            tileWidth: getTiledSize(),
            tileHeight: getTiledSize(),
            tiles: {
              '1': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'wc_b_door_exit',
                'door_exit',
                'door',
              ],
              '?': () => [
                k.sprite('overlay', { anim: 'question' }),
                k.area({ isSensor: true }),
                k.layer('background'),
                'wc_b_info',
                'sfx_emit_action',
                'info',
              ],
            },
          },
        ),
        k.layer('objects'),
        'wc_b',
      ])

      // Set player position
      k.get('player')[0]?.setPos(k.vec2(getTiledSize(2), getTiledSize(6)))

      const doorExitCollision = k.onCollide('player', 'wc_b_door_exit', () => {
        k.flash(k.BLACK, 0.5)
        roomObj.destroy()
        doorExitCollision.cancel()
        infoCollision.cancel()
        infoCollisionEnd.cancel()
        if (roomOpts.onExit) roomOpts.onExit()
      })

      const infoCollision = k.onCollide('player', 'wc_b_info', () => {
        showTextFrame(getLangString('wc_info'))
      })

      const infoCollisionEnd = k.onCollideEnd('player', 'wc_b_info', () => {
        showTextFrame()
      })
    })

    return roomObj
  }

  return {
    setWCB,
  }
}
